/**
 * Shared WebAudio graph. Everything is synthesized, so the game ships no
 * audio files. The context is created on the first user gesture (autoplay
 * rules) and suspended while the page is hidden to save battery.
 *
 *   sfx ──┐                 ┌─> reverb ─> reverbReturn ─┐
 *   music ┼─> master ─> limiter ─> destination            │
 *         └──── send ───────┘          master <──────────┘
 */
export interface AudioGraph {
  readonly ctx: AudioContext;
  readonly sfx: GainNode;
  readonly music: GainNode;
  /** Feed a voice into this for a soft room tail. */
  readonly reverbSend: GainNode;
}

const SFX_LEVEL = 0.9;
const MUSIC_LEVEL = 0.34;

let graph: AudioGraph | null = null;
let sfxOn = true;
let musicOn = true;
const readyListeners = new Set<(graph: AudioGraph) => void>();

function impulse(ctx: AudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < length; i += 1) {
      const t = i / length;
      // Exponential decay with a soft onset: a small, warm room.
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 3.2) * Math.min(1, i / (ctx.sampleRate * 0.012));
    }
  }
  return buffer;
}

function build(ctx: AudioContext): AudioGraph {
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -10;
  limiter.knee.value = 8;
  limiter.ratio.value = 6;
  limiter.attack.value = 0.004;
  limiter.release.value = 0.2;
  limiter.connect(ctx.destination);

  const master = ctx.createGain();
  master.gain.value = 0.9;
  master.connect(limiter);

  const sfx = ctx.createGain();
  sfx.gain.value = sfxOn ? SFX_LEVEL : 0;
  sfx.connect(master);

  const music = ctx.createGain();
  music.gain.value = musicOn ? MUSIC_LEVEL : 0;
  music.connect(master);

  const reverb = ctx.createConvolver();
  reverb.buffer = impulse(ctx, 1.5);
  const reverbReturn = ctx.createGain();
  reverbReturn.gain.value = 0.32;
  reverb.connect(reverbReturn).connect(master);
  const reverbSend = ctx.createGain();
  reverbSend.gain.value = 1;
  reverbSend.connect(reverb);

  return { ctx, sfx, music, reverbSend };
}

export function audioGraph(): AudioGraph | null {
  return graph;
}

/** Runs now if audio is ready, otherwise right after the first gesture. */
export function whenAudioReady(listener: (graph: AudioGraph) => void): void {
  if (graph) listener(graph);
  else readyListeners.add(listener);
}

export function unlockAudio(): void {
  if (typeof window === "undefined") return;
  if (graph) {
    if (graph.ctx.state === "suspended" && document.visibilityState === "visible") void graph.ctx.resume();
    return;
  }
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return;
  const ctx = new Ctor({ latencyHint: "interactive" });
  // iOS only unlocks output after something plays inside the gesture.
  const silent = ctx.createBufferSource();
  silent.buffer = ctx.createBuffer(1, 1, 22050);
  silent.connect(ctx.destination);
  silent.start(0);
  if (ctx.state === "suspended") void ctx.resume();
  graph = build(ctx);
  for (const listener of readyListeners) listener(graph);
  readyListeners.clear();
}

/** Keeps trying on later gestures until the context is actually running. */
export function installAudioUnlock(): void {
  if (typeof window === "undefined") return;
  const events = ["pointerdown", "pointerup", "touchend", "click", "keydown"] as const;
  const handler = () => {
    unlockAudio();
    if (graph?.ctx.state === "running") for (const event of events) window.removeEventListener(event, handler, true);
  };
  for (const event of events) window.addEventListener(event, handler, true);
  document.addEventListener("visibilitychange", () => {
    if (!graph) return;
    if (document.visibilityState === "hidden") void graph.ctx.suspend();
    else void graph.ctx.resume();
  });
}

function ramp(param: AudioParam, value: number, seconds = 0.25): void {
  if (!graph) return;
  const now = graph.ctx.currentTime;
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(value, now + seconds);
}

export function setSfxEnabled(value: boolean): void {
  sfxOn = value;
  if (graph) ramp(graph.sfx.gain, value ? SFX_LEVEL : 0, 0.05);
}

export function sfxEnabled(): boolean {
  return sfxOn;
}

export function setMusicBusEnabled(value: boolean): void {
  musicOn = value;
  if (graph) ramp(graph.music.gain, value ? MUSIC_LEVEL : 0, 0.6);
}

/** Briefly lowers the music under a jingle. */
export function duckMusic(seconds: number, depth = 0.3): void {
  if (!graph || !musicOn) return;
  const gain = graph.music.gain;
  const now = graph.ctx.currentTime;
  gain.cancelScheduledValues(now);
  gain.setValueAtTime(gain.value, now);
  gain.linearRampToValueAtTime(MUSIC_LEVEL * depth, now + 0.08);
  gain.setValueAtTime(MUSIC_LEVEL * depth, now + seconds);
  gain.linearRampToValueAtTime(MUSIC_LEVEL, now + seconds + 1.2);
}

let noise: AudioBuffer | null = null;

/** One second of white noise, shared by shakers and whooshes. */
export function noiseBuffer(ctx: AudioContext): AudioBuffer {
  if (noise && noise.sampleRate === ctx.sampleRate) return noise;
  noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = noise.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  return noise;
}

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}
